import { IAppContext } from 'App/AppContext';
import { TaskViewDTO, TaskViewLayoutDTO } from 'models/dto/tasks/taskViewDTO';
import { logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import { IHubConnection, startHubConnection } from './GeneralHub';

export const TaskViewHubEndpoint = 'hubs/taskviews';

// One hub method on the server (ReceiveTaskViewEvent). The ideal payload has
// a `kind` discriminator, but SignalR's JSON protocol can omit the
// System.Text.Json polymorphism discriminator. The receiver normalizes the
// payload by inferring the kind from its concrete fields before dispatch.
export const ReceiveTaskViewEvent = 'ReceiveTaskViewEvent';

// Discriminated union -- one TypeScript variant per server record. The `kind`
// strings MUST match the server-side [JsonDerivedType] discriminator values:
//   viewMutated   -> TaskViewMutatedEvent
//   viewDeleted   -> TaskViewDeletedEvent
//   layoutMutated -> TaskViewLayoutMutatedEvent
// Adding a new event = add a new arm here and a new switch case in consumers.
// Every event carries a per-user monotonic sequenceNumber assigned by the
// server's ITaskViewEventBuffer -- consumers persist the last seen value and
// pass it back via RequestReplay on reconnect to replay missed events.
export type ITaskViewEvent =
  | { kind: 'viewMutated';   view: TaskViewDTO;          updatedAt: string;       sequenceNumber: number }
  | { kind: 'viewDeleted';   taskViewId: number;          deletedAt: string;       updatedAt: string; sequenceNumber: number }
  | { kind: 'layoutMutated'; layout: TaskViewLayoutDTO;   layoutUpdatedAt: string; updatedAt: string; sequenceNumber: number };

export interface ITaskViewReplayResult {
  events: ITaskViewEvent[];
  currentSequence: number;
  snapshotRequired: boolean;
}

export type TaskViewHubMethodReceiveEvent = (evt: ITaskViewEvent) => void;

type RawTaskViewEvent = {
  kind?: ITaskViewEvent['kind'];
  Kind?: ITaskViewEvent['kind'];
  view?: TaskViewDTO;
  View?: TaskViewDTO;
  layout?: TaskViewLayoutDTO;
  Layout?: TaskViewLayoutDTO;
  taskViewId?: number;
  TaskViewId?: number;
  deletedAt?: string;
  DeletedAt?: string;
  updatedAt?: string;
  UpdatedAt?: string;
  layoutUpdatedAt?: string;
  LayoutUpdatedAt?: string;
  SequenceNumber?: number | string;
  sequenceNumber?: number | string;
};

const getRawKind = (evt: RawTaskViewEvent): ITaskViewEvent['kind'] | undefined =>
  evt.kind ?? evt.Kind;

const getSequenceNumber = (evt: RawTaskViewEvent): number => {
  const raw = evt.sequenceNumber ?? evt.SequenceNumber;
  const parsed = typeof raw === 'number' ? raw : Number(raw);

  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeTaskViewEvent = (evt: RawTaskViewEvent | null | undefined): ITaskViewEvent | null => {
  if (!evt) return null;

  const kind = getRawKind(evt);
  const sequenceNumber = getSequenceNumber(evt);
  const updatedAt = evt.updatedAt ?? evt.UpdatedAt ?? '';
  const view = evt.view ?? evt.View;
  const layout = evt.layout ?? evt.Layout;
  const taskViewId = evt.taskViewId ?? evt.TaskViewId;
  const deletedAt = evt.deletedAt ?? evt.DeletedAt ?? updatedAt;
  const layoutUpdatedAt = evt.layoutUpdatedAt ?? evt.LayoutUpdatedAt ?? updatedAt;

  if ((kind === 'viewMutated' || !kind) && view) {
    return { kind: 'viewMutated', view, updatedAt, sequenceNumber };
  }
  if ((kind === 'viewDeleted' || !kind) && taskViewId !== undefined) {
    return { kind: 'viewDeleted', taskViewId, deletedAt, updatedAt, sequenceNumber };
  }
  if ((kind === 'layoutMutated' || !kind) && layout) {
    return { kind: 'layoutMutated', layout, layoutUpdatedAt, updatedAt, sequenceNumber };
  }

  return null;
};

const getTaskViewEventSummary = (evt: ITaskViewEvent | null) => ({
  kind: evt?.kind,
  sequenceNumber: evt?.sequenceNumber,
  taskViewId:
    evt?.kind === 'viewMutated'
      ? evt.view?.taskViewId
      : evt?.kind === 'viewDeleted'
      ? evt.taskViewId
      : undefined,
  layoutRevision: evt?.kind === 'layoutMutated' ? evt.layout?.revision : undefined,
});

export const startTaskViewHubConnection = async (
  appContext: IAppContext,
  onEvent: TaskViewHubMethodReceiveEvent,
): Promise<IHubConnection | null> => {
  logTaskViewDiagnostic('signalr.taskviews.start', {
    tenantId: appContext.user?.login?.tenantId,
  });

  const connection = await startHubConnection(appContext, TaskViewHubEndpoint, [
    {
      method: ReceiveTaskViewEvent,
      callback: (evt: RawTaskViewEvent) => {
        const normalized = normalizeTaskViewEvent(evt);
        logTaskViewDiagnostic('signalr.taskviews.receive', {
          rawKind: evt ? getRawKind(evt) : undefined,
          rawKeys: evt ? Object.keys(evt) : [],
          ...getTaskViewEventSummary(normalized),
        });
        if (!normalized) {
          logTaskViewDiagnostic('signalr.taskviews.receive.unclassified', {
            rawKeys: evt ? Object.keys(evt) : [],
          });

          return;
        }
        onEvent(normalized);
      },
    },
  ]);

  logTaskViewDiagnostic('signalr.taskviews.start.result', {
    connected: Boolean(connection),
    connectionId: connection?.connectionId,
    state: connection?.connection.state,
  });

  return connection;
};

export const requestTaskViewReplay = async (
  hub: IHubConnection,
  lastSequence: number,
): Promise<ITaskViewReplayResult | null> => {
  logTaskViewDiagnostic('signalr.taskviews.replay.invoke', {
    lastSequence,
    connectionId: hub.connectionId,
    state: hub.connection.state,
  });

  try {
    const result = await hub.connection.invoke<ITaskViewReplayResult>('RequestReplay', lastSequence);
    const normalizedEvents = ((result.events ?? []) as RawTaskViewEvent[])
      .map(normalizeTaskViewEvent)
      .filter((evt): evt is ITaskViewEvent => Boolean(evt));
    logTaskViewDiagnostic('signalr.taskviews.replay.success', {
      lastSequence,
      currentSequence: result.currentSequence,
      eventCount: result.events.length,
      normalizedEventCount: normalizedEvents.length,
      snapshotRequired: result.snapshotRequired,
    });

    return {
      ...result,
      events: normalizedEvents,
    };
  } catch (err) {
    logTaskViewDiagnostic('signalr.taskviews.replay.error', {
      lastSequence,
      code: (err as { code?: string })?.code,
      message: (err as { message?: string })?.message,
    });
    // Replay is best-effort -- if it fails the resync path still fetches
    // canonical state. Returning null lets the Provider fall through.

    return null;
  }
};
